import { Formio } from '@formio/angular';
import download from 'downloadjs';

type FileInfo = {
  url?: string;
  originalName?: string;
  name?: string;
  type?: string;
  storage?: string;
  base64?: string;
  data?: string;
};

const DOWNLOAD_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`;

const REMOVE_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

const INLINE_BUTTON_STYLES = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--Vetai-Spacing-2, 8px)',
  padding: 'var(--Vetai-Spacing-2, 8px) var(--Vetai-Spacing-2-5, 10px)',
  fontSize: 'var(--Vetai-T5, 16px)',
  lineHeight: 'var(--Vetai-24, 24px)',
  fontFamily: 'var(--Vetai-Font-Family, inherit)',
  fontWeight: 'var(--Vetai-Font-Weight-Regular, 400)',
  borderRadius: 'var(--Vetai-Radius-2, 6px)',
  border: '1px solid var(--Vetai-Form_controls--Border_Default, #94a3b8)',
  background: 'var(--Vetai-Backgrounds--Base, #d3dbc5)',
  color: 'var(--Vetai-Form_controls--Text_Default, #152138)',
  cursor: 'pointer',
};

export function getDownloadUrl(fileInfo: FileInfo): string | null {
  if (fileInfo?.url?.startsWith?.('data:')) {
    return fileInfo.url;
  }
  const base64 = fileInfo?.base64 ?? fileInfo?.data;
  if (base64 && typeof base64 === 'string') {
    const mime = fileInfo.type || 'application/octet-stream';
    return `data:${mime};base64,${base64}`;
  }
  return fileInfo?.url || null;
}

function triggerDownload(fileInfo: FileInfo): void {
  const url = getDownloadUrl(fileInfo);
  const name = fileInfo?.originalName || fileInfo?.name || 'download';
  const type = fileInfo?.type;
  if (url) {
    download(url, name, type);
  }
}

export function canDownloadDirectly(fileInfo: FileInfo): boolean {
  return !!getDownloadUrl(fileInfo) || fileInfo?.storage === 'base64' || !!fileInfo?.base64 || !!fileInfo?.data;
}

function createDownloadButton(fileInfo: FileInfo, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'formio-file-download-btn';
  Object.assign(btn.style, INLINE_BUTTON_STYLES);
  const fileName = fileInfo.originalName || fileInfo.name || 'Download';
  const iconSpan = document.createElement('span');
  iconSpan.className = 'formio-file-download-btn-icon';
  iconSpan.style.display = 'inline-flex';
  iconSpan.style.flexShrink = '0';
  iconSpan.innerHTML = DOWNLOAD_ICON_SVG;
  btn.innerHTML = '';
  const textSpan = document.createElement('span');
  textSpan.className = 'formio-file-download-btn-text';
  textSpan.textContent = fileName;
  btn.appendChild(textSpan);
  btn.appendChild(iconSpan);
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    onClick();
  });
  return btn;
}

function createRemoveButton(onClick: () => void): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'formio-file-remove-btn';
  Object.assign(btn.style, INLINE_BUTTON_STYLES);
  btn.style.padding = 'var(--Vetai-Spacing-2, 8px)';
  btn.innerHTML = REMOVE_ICON_SVG;
  btn.setAttribute('aria-label', 'Remove file');
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    onClick();
  });
  return btn;
}

/**
 * Registers a custom Formio File component that ensures:
 * - Hides image previews, shows files as joii-inline-button style buttons
 * - Base64-stored files display correctly with download option
 * - No fileService required for base64/data URL files
 *
 * Call this once after Formio.use(premium) in the app config.
 */
export function registerFormioFileWithDownload(): void {
  const BaseFile = Formio.Components.components['file'];
  if (!BaseFile) return;

  class FileWithDownloadComponent extends BaseFile {
    loadImage() {
      return Promise.resolve('');
    }

    getFile(fileInfo: FileInfo) {
      if (canDownloadDirectly(fileInfo)) {
        triggerDownload(fileInfo);
        return;
      }

      const self = this as typeof this & { fileService?: unknown };
      if (self.fileService) {
        return super.getFile(fileInfo);
      }

      alert('File Service not provided');
    }

    attach(element: HTMLElement) {
      const result = super.attach(element);

      const self = this as typeof this & {
        dataValue?: FileInfo[];
        refs?: { fileLink?: HTMLElement[]; fileImage?: HTMLElement[]; removeLink?: HTMLElement[] };
        disabled?: boolean;
      };
      const files = self.dataValue || [];
      const refs = self.refs ?? {};
      const isDisabled = self.disabled ?? false;

      if (files.length > 0) {
        refs.fileImage?.forEach((img) => (img.style.display = 'none'));
        refs.removeLink?.forEach((link) => (link.style.display = 'none'));

        const fileListContainer =
          element.querySelector(
            '.formio-component-file table, .formio-component-file .fileSelector, .formio-component-file [ref="fileList"]'
          ) ||
          element.querySelector('.formio-component-file') ||
          element;

        const buttonsWrapper = document.createElement('div');
        buttonsWrapper.className = 'formio-file-download-buttons';
        buttonsWrapper.style.display = 'flex';
        buttonsWrapper.style.flexDirection = 'column';
        buttonsWrapper.style.gap = 'var(--Vetai-Spacing-2, 8px)';
        buttonsWrapper.style.marginTop = 'var(--Vetai-Spacing-2, 8px)';

        const table = element.querySelector('.formio-component-file table') as HTMLElement | null;
        if (table) {
          table.style.display = 'none';
        }

        refs.fileLink?.forEach((link) => (link.style.display = 'none'));

        files.forEach((fileInfo: FileInfo) => {
          const row = document.createElement('div');
          row.className = 'formio-file-row';
          row.style.display = 'flex';
          row.style.alignItems = 'center';
          row.style.gap = 'var(--Vetai-Spacing-2, 8px)';

          const downloadBtn = createDownloadButton(fileInfo, () => this.getFile(fileInfo));
          downloadBtn.style.flex = '1';
          downloadBtn.style.minWidth = '0';
          row.appendChild(downloadBtn);

          if (!isDisabled) {
            const removeBtn = createRemoveButton(() =>
              (this as unknown as { handleFileToRemove: (f: FileInfo) => void }).handleFileToRemove(fileInfo)
            );
            row.appendChild(removeBtn);
          }

          buttonsWrapper.appendChild(row);
        });

        const existingWrapper = element.querySelector('.formio-file-download-buttons');
        if (existingWrapper) {
          existingWrapper.replaceWith(buttonsWrapper);
        } else {
          fileListContainer.appendChild(buttonsWrapper);
        }
      }

      return result;
    }
  }

  Formio.Components.addComponent('file', FileWithDownloadComponent);
}
